import { oneSpec } from '@bemor/web-kit';
import {
    AsOfBadge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DetailRow,
    FieldGrid,
    Money,
    ObjectRef,
    PageHeader,
    PublicId,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { Link, useParams } from 'react-router-dom';

import type { StaffPaymentMethod } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { useOne } from '@/lib/queries';
import {
    defaultLabel,
    MethodCard,
    MethodCustomer,
    MethodSystem,
    offeredLabel,
} from './index';

/** The reads of `/payment-methods/:id` (BM-46 §2.0). */
export const paymentMethodReads = (id: string | undefined) => ({
    method: oneSpec(`/staff/payment-methods/${id ?? ''}`),
});

/**
 * bm-111 § 21 (D-18): one saved payment method as staff read it: its facts, whether the hosted page shows it to its
 * buyer, and the charges that used it (newest 50), each linking to its charge page. Never a vault id or card data.
 */
export function PaymentMethodPage() {
    const { id = '' } = useParams();
    const query = useOne<StaffPaymentMethod>(
        paymentMethodReads(id).method.path,
    );
    return (
        <div>
            <PageHeader
                crumbs={[
                    { label: 'Payment methods', href: '/payment-methods' },
                ]}
                {...detailTitle(query, null, id, 'Payment method')}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
            />
            <DetailBody query={query} title="Payment method">
                {(m) => (
                    <div className="space-y-4">
                        <Card data-testid="method-facts">
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Payment method
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <FieldGrid>
                                    <DetailRow label="Status" layout="stacked">
                                        <StatusBadge status={m.status} />
                                    </DetailRow>
                                    <DetailRow
                                        label="Show to customer"
                                        layout="stacked"
                                    >
                                        {offeredLabel(m)}
                                    </DetailRow>
                                    <DetailRow
                                        label="Payment system"
                                        layout="stacked"
                                    >
                                        <MethodSystem method={m} />
                                    </DetailRow>
                                    <DetailRow label="Card" layout="stacked">
                                        <MethodCard method={m} />
                                    </DetailRow>
                                    <DetailRow label="BIN" layout="stacked">
                                        <span className="font-mono">
                                            {m.bin}
                                        </span>
                                    </DetailRow>
                                    <DetailRow
                                        label="Issuer country"
                                        layout="stacked"
                                    >
                                        {m.issuer_country ?? '—'}
                                    </DetailRow>
                                    <DetailRow
                                        label="Is Default"
                                        layout="stacked"
                                    >
                                        {defaultLabel(m)}
                                    </DetailRow>
                                    <DetailRow
                                        label="Merchant"
                                        layout="stacked"
                                    >
                                        <span className="flex min-w-0 flex-col">
                                            {m.merchant.name ? (
                                                <span>{m.merchant.name}</span>
                                            ) : null}
                                            <ObjectRef id={m.merchant.id} />
                                        </span>
                                    </DetailRow>
                                    <DetailRow
                                        label="Customer"
                                        layout="stacked"
                                    >
                                        <MethodCustomer method={m} />
                                    </DetailRow>
                                    <DetailRow label="Created" layout="stacked">
                                        <DateTime
                                            zone="inline"
                                            value={m.created_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                    <DetailRow label="Updated" layout="stacked">
                                        <DateTime
                                            zone="inline"
                                            value={m.updated_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                </FieldGrid>
                            </CardContent>
                        </Card>
                        <Card data-testid="method-charges">
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Charges
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {m.charges?.length ? (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>
                                                        Charge
                                                    </TableHead>
                                                    <TableHead>State</TableHead>
                                                    <TableHead>
                                                        Amount
                                                    </TableHead>
                                                    <TableHead>
                                                        Created
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {m.charges.map((c) => (
                                                    <TableRow key={c.id}>
                                                        <TableCell>
                                                            <Link
                                                                to={`/money/charges/${encodeURIComponent(c.id)}`}
                                                                className="font-mono text-xs hover:underline"
                                                            >
                                                                {c.id}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell>
                                                            <StatusBadge
                                                                status={c.state}
                                                                domain="charge"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Money
                                                                cents={
                                                                    c.amount_cents
                                                                }
                                                                currency="USD"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <DateTime
                                                                value={
                                                                    c.created_at
                                                                }
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-sm">
                                        No charge has used this payment method.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}
            </DetailBody>
        </div>
    );
}
